import type { FilterData, SelectedNbfFilters } from '@which/shared'

import type {
  Filter as FilterType,
  NeedsBasedAnswerOption,
  NeedsBasedFilter,
} from '../../../../../generated/frontend'
import type {
  AppliedFilterItem,
  FilterItemIntersection,
  NeedsBasedFilterAnswersExtended,
} from '../../../../../shared/types'
import { getFilterAttributeValue } from '../Filter/utils'

export const mapAppliedFilterItems = ({
  activeFilter,
  basicFilterData,
}: {
  activeFilter?: Partial<FilterType | NeedsBasedAnswerOption>
  basicFilterData: FilterData
}): AppliedFilterItem[] => {
  return getAppliedMappedFiltersItems({ activeFilter, basicFilterData })
}
const getAppliedMappedFiltersItems = ({ activeFilter, basicFilterData }) => {
  if (activeFilter?.filterItems) {
    return activeFilter.filterItems.flatMap((filterItem: FilterItemIntersection) => {
      const { dropdownAppliedFilterItem, exclusionAppliedFilterItem, defaultAppliedFilterItem } =
        appliedFiltersItems({
          activeFilter,
          basicFilterData,
        })
      switch (true) {
        case activeFilter.componentType === 'dropdown':
          return dropdownAppliedFilterItem(filterItem)
        case activeFilter.exclusion:
          return exclusionAppliedFilterItem(filterItem)
        default:
          return defaultAppliedFilterItem(filterItem)
      }
    })
  }

  return [
    {
      code: activeFilter.code,
      filterLabel: '',
      value: [],
      path: `${activeFilter.externalId}.${activeFilter.code}`,
      label: `${capitalize(activeFilter.label || '')}`,
      questionText: activeFilter.questionText,
      nbfLabel: activeFilter.nbfLabel,
      needsBasedOptions: activeFilter.needsBasedOptions,
    },
  ]
}
const appliedFiltersItems = ({
  activeFilter,
  basicFilterData,
}: {
  activeFilter: FilterType
  basicFilterData: FilterData
}) => {
  const { externalId, filterLabel } = activeFilter
  const filterDataSegment = basicFilterData[externalId]
  return {
    dropdownAppliedFilterItem: (item: FilterItemIntersection) => {
      const { label, from, code, to } = item

      const dropdownKeys = Object.keys(filterDataSegment)
      const dropdownKey = dropdownKeys?.find((key) => {
        const valueToCheck = key === 'to' ? String(to) : String(from)
        return filterDataSegment[key].includes(valueToCheck)
      })

      return dropdownKey
        ? [
            {
              code,
              filterLabel,
              value: [],
              path: `${externalId}.${dropdownKey}`,
              label: `${filterLabel} ${dropdownLabelMap(dropdownKey)}: ${label}`,
            },
          ]
        : []
    },
    exclusionAppliedFilterItem: (item: FilterItemIntersection) => {
      const { label, code, options } = item
      if (code && filterDataSegment[code]) {
        return (
          options &&
          options.flatMap(({ optionValueBool, optionValueStr, label: optionLabel }) => {
            const optionValue = String(optionValueStr || optionValueBool)
            return filterDataSegment[code].includes(optionValue)
              ? [
                  {
                    code,
                    filterLabel,
                    value: optionValue,
                    path: `${externalId}.${code}`,
                    /* we need the "or" condition here as some of filters were converted to exclusion
                    in reviews-filter-definitions package, but they don't have a bucket label*/
                    label: `${label || filterLabel}: ${capitalize(optionLabel || '')}`,
                  },
                ]
              : []
          })
        )
      }
      return []
    },
    defaultAppliedFilterItem: (item: FilterItemIntersection) => {
      const { code, valueBool, valueStr, from, to, label } = item
      const value = getFilterAttributeValue({ valueBool, valueStr, from, to })

      return code && filterDataSegment[code]?.includes(value)
        ? [
            {
              code,
              value,
              filterLabel,
              path: `${externalId}.${code}`,
              label: label,
            },
          ]
        : []
    },
  }
}

export const getActiveNeedsBasedFilters = ({
  needsBasedFilters,
  selectedNeedsBasedFilters,
  needsBasedFilterData,
}: GetActiveNeedsBasedFiltersArgs) => {
  const currentlySelectedNeedsBasedFilters = needsBasedFilters.filter(
    (needsBasedFilter) => selectedNeedsBasedFilters?.[needsBasedFilter.questionText]
  )

  const activeNeedsBasedFilterAnswers = currentlySelectedNeedsBasedFilters.reduce(
    (needsBasedFilterAnswers: NeedsBasedFilterAnswersExtended[], activeNeedsBasedFilter) => {
      const [activeAnswer] = activeNeedsBasedFilter.answers.filter((answer) =>
        answer.needsBasedAnswerOptions.some(
          (option) =>
            selectedNeedsBasedFilters?.[activeNeedsBasedFilter.questionText]?.hasOwnProperty(
              answer.label
            ) && doesNeedsBasedFilterIncludeOptionValue({ needsBasedFilterData, option })
        )
      )

      if (activeAnswer) {
        const extendedActiveAnswer = {
          ...activeAnswer,
          questionText: activeNeedsBasedFilter.questionText,
        }

        needsBasedFilterAnswers.push(extendedActiveAnswer)
      }

      return needsBasedFilterAnswers
    },
    []
  )

  return activeNeedsBasedFilterAnswers.flatMap((activeAnswer) => {
    const exclusiveNeedsBasedOptions = activeAnswer.needsBasedAnswerOptions.filter((option) =>
      doesNeedsBasedFilterIncludeOptionValue({ needsBasedFilterData, option })
    )

    return exclusiveNeedsBasedOptions.map((option) => {
      return {
        ...option,
        nbfLabel: activeAnswer.label,
        questionText: activeAnswer.questionText,
        needsBasedOptions: activeAnswer.needsBasedAnswerOptions,
      }
    })
  })
}

const doesNeedsBasedFilterIncludeOptionValue = ({
  needsBasedFilterData,
  option,
}: {
  needsBasedFilterData?: FilterData
  option: NeedsBasedAnswerOption
}) => {
  const { valueBool, valueStr, from, to, externalId, code } = option
  return needsBasedFilterData?.[externalId]?.[code]?.includes(
    getFilterAttributeValue({ valueBool, valueStr, from, to })
  )
}

const minMaxMap = {
  from: 'min',
  to: 'max',
}

const capitalize = (word: string) => {
  return word.charAt(0).toUpperCase() + word.slice(1)
}

export const dropdownLabelMap = (label: string) => minMaxMap[label] || label

type GetActiveNeedsBasedFiltersArgs = {
  needsBasedFilters: NeedsBasedFilter[]
  selectedNeedsBasedFilters?: SelectedNbfFilters
  needsBasedFilterData?: FilterData
}
